// EasterEggHunt.tsx
import VillageModal from "@/components/modals/VillageModal";
import { CLAIM_EASTER_EGG_MUTATION } from "@/graphql/mutations/easterEggMutations";
import { POLL_EASTER_EGG_QUERY } from "@/graphql/queries/easterEggQueries";
import { EASTER_EGG_POLL_SUBSCRIPTION } from "@/graphql/subscriptions/easterEggSubscriptions";
import {
    ClaimEasterEggResult,
    ClaimEasterEggVars,
    Egg,
    EggProps,
    PollEasterEggResult,
} from "@/types/easterEggTypes";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import React, { useEffect, useState } from "react";
import EasterEggSuccessModal from "./EasterEggSuccessModal";

// The EggComponent displays the egg image at a random position.
const EggComponent: React.FC<EggProps> = ({ egg, onClick }) => {
    const [position, setPosition] = useState<{ top: string; left: string }>({
        top: "50%",
        left: "50%",
    });

    useEffect(() => {
        // Randomize position between 10% and 80% of the viewport.
        const top = Math.floor(Math.random() * 70) + 10;
        const left = Math.floor(Math.random() * 70) + 10;
        setPosition({ top: `${top}%`, left: `${left}%` });
    }, []);

    return (
        <div
            onClick={onClick}
            className="absolute z-500 cursor-pointer select-none"
            style={{ top: position.top, left: position.left }}
        >
            <img src={egg.imageUrl} alt={egg.name} className="h-12 w-12" />
        </div>
    );
};

const EasterEggHunt: React.FC = () => {
    // Initial query to get egg data
    const { data, loading, refetch } = useQuery<PollEasterEggResult>(
        POLL_EASTER_EGG_QUERY,
        {
            fetchPolicy: "cache-and-network",
        },
    );

    // Subscribe to real-time egg updates
    useSubscription(EASTER_EGG_POLL_SUBSCRIPTION, {
        onData: ({ data: subscriptionData }) => {
            console.debug(
                "[EasterEggHunt] Subscription data received:",
                subscriptionData,
            );
            if (subscriptionData?.data?.easterEggPoll) {
                console.debug(
                    "[EasterEggHunt] Easter egg data from subscription:",
                    subscriptionData.data.easterEggPoll,
                );
            }
            refetch();
        },
    });

    const [claimEasterEgg] = useMutation<
        ClaimEasterEggResult,
        ClaimEasterEggVars
    >(CLAIM_EASTER_EGG_MUTATION);
    const [eggData, setEggData] = useState<{ egg: Egg; token: string } | null>(
        null,
    );
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!loading && data) {
            console.debug("[EasterEggHunt] Query data received:", data);
            if (
                data.pollEasterEgg &&
                data.pollEasterEgg.egg &&
                data.pollEasterEgg.token
            ) {
                console.debug(
                    "[EasterEggHunt] Egg available:",
                    data.pollEasterEgg.egg.name,
                    "token:",
                    data.pollEasterEgg.token,
                );
                setEggData({
                    egg: data.pollEasterEgg.egg,
                    token: data.pollEasterEgg.token,
                });
            } else {
                console.debug("[EasterEggHunt] No egg available");
                setEggData(null);
            }
        }
    }, [data, loading]);

    const handleEggClick = async () => {
        if (!eggData) return;
        console.debug(
            "[EasterEggHunt] Egg clicked, claiming with token:",
            eggData.token,
        );
        try {
            const result = await claimEasterEgg({
                variables: { instanceId: eggData.token },
            });
            console.debug("[EasterEggHunt] Egg claimed successfully:", result);
            setModalVisible(true);
        } catch (error: any) {
            console.error("[EasterEggHunt] Error claiming egg:", error);
            setErrorMessage(error.message);
            setErrorModalOpen(true);
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        setEggData(null);
        refetch();
    };

    return (
        <>
            {/* Egg overlay: the egg is rendered in a fixed container that floats above other content */}
            {/* <div className="fixed inset-0 z-50"> */}
            {eggData && (
                <EggComponent egg={eggData.egg} onClick={handleEggClick} />
            )}
            {/* </div> */}

            {/* Easter Egg Success Modal */}
            {eggData && (
                <EasterEggSuccessModal
                    isOpen={modalVisible}
                    closeModal={closeModal}
                    egg={eggData.egg}
                />
            )}

            {/* Error Modal */}
            <VillageModal
                isOpen={errorModalOpen}
                onClose={() => {
                    setErrorModalOpen(false);
                }}
                title="Error"
                message={errorMessage}
                type="error"
            />
        </>
    );
};

export default EasterEggHunt;
